// features/workstations/knowledge/research-section.js
// ═══════════════════════════════════════════════════════════
// Research & Breakthroughs — curated developments in the
// domain. Sorted by RELEVANCE TO THE USER'S SETUP, not just
// recency (per Tim's design).
//
// V1: queries workstation_knowledge_entries WHERE kind IN
// ('research','tool','regulatory'). Empty until items are
// added (admin endpoint TBD; for now, manual SQL inserts or
// future curator tooling).
//
// Relevance ranking V1: tags-based.
//   relevance_tags array on the entry intersected with the
//   user's setup_types from workstation config — more matches
//   = higher relevance score.
// V2: ML / per-user engagement signal.
// ═══════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { ShiftRead, SourceTrace } from '../shared';

const KIND_TONES = {
  research:    'teal',
  tool:        'sage',
  regulatory:  'terra',
};
const KIND_LABELS = {
  research:    'Research',
  tool:        'Tool Update',
  regulatory:  'Regulatory',
};

export default function ResearchSection({ workstation, userId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState('relevance');

  useEffect(() => {
    if (!workstation?.domain_key) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qs = new URLSearchParams({
          domain: workstation.domain_key,
          kind: 'research',
        });
        if (userId) qs.set('user_id', userId);
        const res = await fetch(`/api/workstations/knowledge?${qs}`, { cache: 'no-store' });
        if (cancelled) return;
        const data = res.ok ? await res.json() : { entries: [] };
        setEntries(data.entries || []);
      } catch (e) {
        console.warn('[knowledge/research] load failed:', e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workstation?.domain_key, userId]);

  // Compute the user's setup tags for relevance scoring
  const userSetupTags = useMemo(() => {
    const setups = workstation?.config?.criteria?.setup_types
                 || workstation?.config?.criteria?.setups
                 || [];
    const tags = new Set();
    for (const s of setups) {
      const label = (s.label || s.name || s || '').toString().toLowerCase();
      tags.add(label);
      // Common token splits
      label.split(/[\s_-]+/).forEach((tok) => { if (tok.length > 2) tags.add(tok); });
    }
    return tags;
  }, [workstation]);

  const ranked = useMemo(() => {
    const scored = entries.map((e) => {
      const tags = (e.relevance_tags || []).map((t) => String(t).toLowerCase());
      let score = 0;
      for (const t of tags) {
        if (userSetupTags.has(t)) score += 2;
        for (const userTag of userSetupTags) {
          if (t.includes(userTag) || userTag.includes(t)) score += 1;
        }
      }
      return { ...e, _relevance: score };
    });
    if (sortMode === 'relevance') {
      scored.sort((a, b) => (b._relevance - a._relevance) || (toTime(b.published_at) - toTime(a.published_at)));
    } else {
      scored.sort((a, b) => toTime(b.published_at) - toTime(a.published_at));
    }
    return scored;
  }, [entries, userSetupTags, sortMode]);

  return (
    <div className="ws-research-section">
      <div className="ws-research-toolbar">
        <span className="ws-research-toolbar-label">Sort by:</span>
        <button
          type="button"
          className={`ws-research-sort${sortMode === 'relevance' ? ' active' : ''}`}
          onClick={() => setSortMode('relevance')}
        >
          Relevant to my setup
        </button>
        <button
          type="button"
          className={`ws-research-sort${sortMode === 'recent' ? ' active' : ''}`}
          onClick={() => setSortMode('recent')}
        >
          Most recent
        </button>
      </div>

      {loading && <div className="ws-loading">Loading research…</div>}

      {!loading && ranked.length === 0 && (
        <div className="ws-feed-empty">
          <div className="ws-feed-empty-title">No research items yet</div>
          <div className="ws-feed-empty-body">
            Curated breakthroughs (academic studies, broker tool updates,
            FINRA regulatory notes) will appear here as they're added.
            <strong> V1 ships with manual curation</strong> — the curator
            adds 1-2 items per week. RSS subscriptions and web-search
            sourcing arrive in V2.
          </div>
        </div>
      )}

      {!loading && ranked.length > 0 && (
        <div className="ws-research-list">
          {ranked.map((e) => <ResearchEntry key={e.id} entry={e} />)}
        </div>
      )}
    </div>
  );
}

function ResearchEntry({ entry }) {
  const tone = KIND_TONES[entry.kind] || 'static';
  const isHigh = entry._relevance >= 3;
  const sources = [];
  if (entry.source_url) sources.push({ label: entry.source_name || 'Source', kind: 'doc', href: entry.source_url });

  return (
    <article className={`ws-research-item ws-top-accent-${tone}`}>
      <div className="ws-research-item-header">
        <span className={`ws-pill ws-pill-${tone}`}>{KIND_LABELS[entry.kind] || 'Item'}</span>
        {isHigh && <span className="ws-pill ws-pill-sage">High relevance to your setup</span>}
        {entry.published_at && (
          <span className="ws-research-item-date">{formatDate(entry.published_at)}</span>
        )}
      </div>
      <h3 className="ws-research-item-title">{entry.title}</h3>
      <div className="ws-research-item-body">{entry.body_md}</div>
      {sources.length > 0 && <SourceTrace sources={sources} />}
      {entry.relevance_tags?.length > 0 && (
        <ShiftRead variant="teal" label="Why this is relevant to your setup">
          Tagged for: {entry.relevance_tags.join(', ')}.
          {isHigh
            ? ' Strong overlap with your setup criteria — worth a careful read.'
            : ' Some overlap — skim for applicable bits.'}
        </ShiftRead>
      )}
    </article>
  );
}

function toTime(d) {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}
